import { forwardRef } from 'react';
import type { TFunction } from 'i18next';
import SurveyQuestion, { type QuestionError } from './SurveyQuestion';
import { headingStyle, pickText } from './surveyLogic';
import type {
  AnswerMap,
  AnswerState,
  SurveyDish,
  SurveyQuestion as Question,
  SurveySection as Section,
} from './types';

interface Props {
  section: Section;
  questions: Question[];
  answers: AnswerMap;
  errors: Record<string, QuestionError>;
  lang: string;
  defaultLang: string;
  dishes: SurveyDish[];
  today: string;
  t: TFunction;
  onChange: (questionId: string, next: AnswerState) => void;
}

/** SC-1752 — one screen = one section; the heading takes focus when the screen changes. */
const SurveySection = forwardRef<HTMLHeadingElement, Props>(function SurveySection(
  { section, questions, answers, errors, lang, defaultLang, dishes, today, t, onChange },
  headingRef,
) {
  const description = pickText(section.description, lang, defaultLang);
  return (
    <section className="flex flex-col gap-6">
      <div>
        <h2 ref={headingRef} tabIndex={-1} style={headingStyle(lang)} className="font-display text-2xl text-ink outline-none">
          {pickText(section.title, lang, defaultLang)}
        </h2>
        {description && <p className="font-mono text-sm text-ink-mute mt-1">{description}</p>}
      </div>
      {questions.map((q) => (
        <SurveyQuestion
          key={q.id}
          question={q}
          answer={answers[q.id]}
          error={errors[q.id]}
          lang={lang}
          defaultLang={defaultLang}
          dishes={dishes}
          today={today}
          t={t}
          onChange={(next) => onChange(q.id, next)}
        />
      ))}
    </section>
  );
});

export default SurveySection;
